// src/pages/ProductsPage.jsx
import { useEffect, useState, useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { fetchProducts } from "../services/sinaliteService";
import { fetchAllCategoryData, buildMergedGroups } from "../utils/categoryTree";
import { doc, getDoc } from "../services/mysqlStore";
import { db } from "../services/mysqlStore";
import "./ProductsPage.css";

const PrinterIcon = () => (
  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="6 9 6 2 18 2 18 9"/>
    <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"/>
    <rect x="6" y="14" width="12" height="8"/>
  </svg>
);

const ChevronIcon = ({ open }) => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ transform: open ? "rotate(90deg)" : "rotate(0deg)", transition: "transform 0.2s" }}>
    <polyline points="9 18 15 12 9 6"/>
  </svg>
);

// Top-level category groups now come from src/constants/navCategories.js
// (DEFAULT_NAV_GROUPS) merged with the admin's category-group overrides —
// see src/utils/categoryTree.js. This keeps the sidebar here in sync with
// the navbar's mega menu automatically. Every sub-category is always a real
// SinaLite category, so a single `cat` query param is enough to identify one.

export default function ProductsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeCat    = searchParams.get("cat")   || "";
  const activeGroup  = searchParams.get("group") || "";
  const activeQuery  = (searchParams.get("q") || "").trim();

  const [allProducts, setAllProducts] = useState([]);
  const [overrides, setOverrides] = useState({});
  const [productOverrides, setProductOverrides] = useState({});
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState("");

  const mergedGroups = useMemo(() => buildMergedGroups({
    allProducts,
    overrides,
    productOverrides,
  }), [allProducts, overrides, productOverrides]);

  // Find which top-level group a real category currently belongs to
  function findParentGroup(catName) {
    const g = mergedGroups.find(g => g.subs.some(s => s.cat === catName));
    return g ? g.label : null;
  }
  function findSub(catName) {
    for (const g of mergedGroups) {
      const s = g.subs.find(s => s.cat === catName);
      if (s) return s;
    }
    return null;
  }

  // Which top-level groups are open
  const [openGroups, setOpenGroups] = useState({});

  useEffect(() => {
    setLoading(true);
    Promise.all([
      fetchProducts(),
      fetchAllCategoryData(),
    ])
      .then(([{ allProducts }, { overrides, productOverrides }]) => {
        setAllProducts(allProducts);
        setOverrides(overrides);
        setProductOverrides(productOverrides);
      })
      .catch(() => setError("Failed to load products. Please try again."))
      .finally(() => setLoading(false));
  }, []);

  // Apply the admin-configured product card background color, if set
  useEffect(() => {
    getDoc(doc(db, "siteSettings", "siteContent")).then(snap => {
      const color = snap.exists() && snap.data().productCardBackgroundColor;
      if (color) document.documentElement.style.setProperty("--product-card-bg", color);
    }).catch(() => {});
  }, []);

  // Auto-open parent group when URL changes + scroll to products grid
  useEffect(() => {
    if (activeCat) {
      const parent = findParentGroup(activeCat);
      if (parent) setOpenGroups(prev => ({ ...prev, [parent]: true }));
    }
    // Scroll to products grid when category/group/search changes
    if (activeCat || activeGroup || activeQuery) {
      const el = document.querySelector(".products-main");
      if (el) {
        const top = el.getBoundingClientRect().top + window.scrollY - 90;
        window.scrollTo({ top, behavior: "smooth" });
      }
    }
  }, [activeCat, activeGroup, activeQuery, mergedGroups.length]);

  const filtered = useMemo(() => {
    let list = allProducts;

    if (activeCat) {
      const sub = findSub(activeCat);
      list = sub ? sub.products : list.filter(p => p.category === activeCat);
    } else if (activeGroup) {
      const group = mergedGroups.find(g => g.label === activeGroup);
      if (group) {
        const ids = new Set();
        group.subs.forEach(s => s.products.forEach(p => ids.add(p.id)));
        list = list.filter(p => ids.has(p.id));
      }
    }

    if (activeQuery) {
      const q = activeQuery.toLowerCase();
      list = list.filter(p => {
        const nameMatch  = p.name && p.name.toLowerCase().includes(q);
        const catMatch   = p.category && p.category.toLowerCase().includes(q);
        const parentGroup = findParentGroup(p.category); // e.g. "Postcards" → "Stationery"
        const groupMatch = parentGroup && parentGroup.toLowerCase().includes(q);
        return nameMatch || catMatch || groupMatch;
      });
    }

    return list;
  }, [allProducts, activeCat, activeGroup, activeQuery, mergedGroups]);

  // If the search query exactly matches a top-level group name (e.g. "stationery"
  // matching the "Stationery" group), highlight/expand that group in the sidebar.
  const matchedGroupLabel = useMemo(() => {
    if (!activeQuery) return null;
    const q = activeQuery.toLowerCase();
    const exact = mergedGroups.find(g => g.label.toLowerCase() === q);
    return exact ? exact.label : null;
  }, [activeQuery, mergedGroups]);

  // If the search query exactly matches a sub-category name (e.g. "postcards"),
  // highlight that sub-category and expand its parent group in the sidebar.
  const matchedSubCat = useMemo(() => {
    if (!activeQuery) return null;
    const q = activeQuery.toLowerCase();
    for (const g of mergedGroups) {
      const exact = g.subs.find(s => s.label.toLowerCase() === q);
      if (exact) return exact;
    }
    return null;
  }, [activeQuery, mergedGroups]);

  useEffect(() => {
    if (matchedGroupLabel) {
      setOpenGroups(prev => ({ ...prev, [matchedGroupLabel]: true }));
    }
    if (matchedSubCat) {
      const parent = findParentGroup(matchedSubCat.cat);
      if (parent) setOpenGroups(prev => ({ ...prev, [parent]: true }));
    }
  }, [matchedGroupLabel, matchedSubCat]);

  function toggleGroup(label) {
    setOpenGroups(prev => ({ ...prev, [label]: !prev[label] }));
  }

  function selectGroup(label) {
    setOpenGroups(prev => ({ ...prev, [label]: true }));
    setSearchParams({ group: label });
  }

  function selectSub(sub) {
    setSearchParams({ cat: sub.cat });
  }

  const heroTitle = activeQuery
    ? `Search results for "${activeQuery}"`
    : activeCat || (activeGroup ? activeGroup : "All Products");

  return (
    <div className="products-page">
      <div className="products-hero">
        <div className="container reveal">
          <span className="tag">Print Products</span>
          <h1 className="section-title">{heroTitle}</h1>
          <p className="section-subtitle" style={{ maxWidth: "100%", width: "100%" }}>
            High-quality printing at trade prices. Fast turnaround across Canada & US.
          </p>
        </div>
      </div>

      <div className="container products-layout">
        {/* Sidebar */}
        <aside className="products-sidebar reveal-left">
          <div className="sidebar-cats">
            <h3>Categories</h3>

            {/* All Products */}
            <button
              className={`cat-filter-btn ${!activeCat && !activeGroup && !activeQuery ? "active" : ""}`}
              onClick={() => setSearchParams({})}
            >
              All Products
            </button>

            {/* Top-level groups with expandable subcategories */}
            {mergedGroups.map(group => {
              const isOpen = !!openGroups[group.label];
              const isGroupActive = activeGroup === group.label || matchedGroupLabel === group.label;
              const hasActiveCat = group.subs.some(s =>
                (activeCat && s.cat === activeCat) ||
                (matchedSubCat && s.label === matchedSubCat.label)
              );

              return (
                <div key={group.slug} className="cat-group">
                  {/* Parent group button */}
                  <button
                    className={`cat-group-btn ${isGroupActive || hasActiveCat ? "active" : ""}`}
                    onClick={() => {
                      toggleGroup(group.label);
                      if (!isOpen) selectGroup(group.label);
                    }}
                  >
                    <span>{group.label}</span>
                    <ChevronIcon open={isOpen} />
                  </button>

                  {/* Subcategories */}
                  {isOpen && (
                    <div className="cat-subs">
                      {group.subs.map(sub => {
                        const isActive = (activeCat && sub.cat === activeCat) ||
                          (matchedSubCat && sub.label === matchedSubCat.label);
                        return (
                          <button
                            key={sub.cat}
                            className={`cat-sub-btn ${isActive ? "active" : ""}`}
                            onClick={() => selectSub(sub)}
                          >
                            {sub.label}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          <div className="sidebar-cta">
            <p>Need a custom quote?</p>
            <Link to="/contact" className="btn-primary" style={{ display: "block", textAlign: "center", marginTop: 10 }}>
              Request Quote
            </Link>
          </div>
        </aside>

        {/* Grid */}
        <main className="products-main">
          {loading ? (
            <div className="products-loading">
              {[1,2,3,4,5,6].map(i => <div key={i} className="skeleton-card shimmer" />)}
            </div>
          ) : error ? (
            <div className="empty-state reveal"><p>{error}</p></div>
          ) : filtered.length === 0 ? (
            <div className="empty-state reveal">
              <p>{activeQuery ? `No results for "${activeQuery}"` : "No products found in this category."}</p>
            </div>
          ) : (
            <div className="products-grid-main">
              {filtered.map((p, i) => (
                <Link
                  to={p.isCustom ? `/products/custom/${p.id}` : `/products/${p.id}`}
                  key={p.isCustom ? `custom_${p.id}` : p.id}
                  className={`product-card-main reveal delay-${(i % 6) + 1}`}
                >
                  <div className="product-img-wrap img-zoom">
                    {p.imageUrl
                      ? <img src={p.imageUrl} alt={p.name} />
                      : <div className="product-img-placeholder"><PrinterIcon /></div>
                    }
                  </div>
                  <div className="product-card-body">
                    <span className="product-category-label">{p.category}</span>
                    <h3>{p.name}</h3>
                    {p.description && <p className="product-desc">{p.description}</p>}
                    <div className="product-card-footer">
                      <span className="btn-primary" style={{ fontSize: 13, padding: "8px 18px" }}>Order Now →</span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </main>
      </div>
    </div>
  );
}
